/**
 * Hook d'inscription Mobile SSO v1.0
 * Gère le flow: init → verify-otp → complete
 * 
 * @version 2.7.9
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { mobileRegistrationService } from '../services/mobileRegistration';
import { nativeAuthService } from '../services/nativeAuth';
import { setNativeAuthConfig, ApiError } from '../services/api';
import type { 
  MobileRegistrationFormData,
  AccountType,
} from '../types/mobile';

interface RegistrationConflict {
  type: 'email' | 'phone';
  masked_identifier: string;
  options: {
    can_login: boolean;
    can_recover_by_email: boolean;
    can_recover_by_sms: boolean;
    masked_email?: string;
    masked_phone?: string;
  };
}

interface ApiConflictResponse {
  conflict?: RegistrationConflict;
  message?: string;
  error_type?: string;
}

interface RegistrationState {
  processToken: string | null;
  status: 'idle' | 'pending_otp' | 'pending_password' | 'pending_registration' | 'completed';
  loading: boolean;
  error: string | null;
  conflict: RegistrationConflict | null;
  otpMethod: 'email' | 'sms' | null;
  otpSentTo: string | null;
}

function getErrorMessage(err: unknown, context: string): string {
  if (err instanceof Error) {
    if (err.message.includes('fetch') || err.message.includes('network')) {
      return 'Vérifiez votre connexion Internet';
    }
    if (err.message.includes('timeout')) {
      return 'Le serveur met trop de temps à répondre';
    }
    return err.message;
  }
  return `Erreur lors de ${context}`;
}

export interface UseMobileRegistrationOptions {
  saasApiUrl: string;
  iamApiUrl: string;
  
}

export function useMobileRegistration(options?: UseMobileRegistrationOptions) {
  const configuredRef = useRef(false);

  useEffect(() => {
    if (options && !configuredRef.current) {
      setNativeAuthConfig({ 
        saasApiUrl: options.saasApiUrl, 
        iamApiUrl: options.iamApiUrl,
      });
      configuredRef.current = true;
    }
  }, [options]);

  const [state, setState] = useState<RegistrationState>({
    processToken: null,
    status: 'idle',
    loading: false,
    error: null,
    conflict: null,
    otpMethod: null,
    otpSentTo: null,
  });

  const [formData, setFormData] = useState<Partial<MobileRegistrationFormData>>({});
  const [accountType, setAccountType] = useState<AccountType>('email');

  const updateFormData = useCallback((data: Partial<MobileRegistrationFormData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  }, []);

  const initRegistration = useCallback(async (
    data: Omit<MobileRegistrationFormData, 'password' | 'password_confirmation'>
  ) => {
    setState(prev => ({ ...prev, loading: true, error: null, conflict: null }));
    
    const enrichedData = {
      ...data,
      email: accountType === 'phone-only' ? '' : data.email,
      registration_type: accountType === 'phone-only' ? 'phone' as const : 'email' as const,
    };
    
    setFormData(enrichedData);

    try {
      if (!nativeAuthService.hasCredentials()) {
        await nativeAuthService.loadCredentials();
      }

      const response = await mobileRegistrationService.init(enrichedData);

      if (response.success && response.process_token) {
        setState(prev => ({
          ...prev,
          processToken: response.process_token!,
          status: 'pending_otp',
          otpMethod: response.otp_method || prev.otpMethod,
          otpSentTo: response.otp_sent_to || prev.otpSentTo,
          loading: false,
          conflict: null,
        }));
        return { 
          success: true, 
          otp_code_dev: response.otp_code_dev,
          otp_method: response.otp_method,
          otp_sent_to: response.otp_sent_to,
        };
      }

      if (response.conflict) {
        setState(prev => ({
          ...prev,
          loading: false,
          error: response.message || 'Ce compte existe déjà',
          conflict: response.conflict!,
        }));
        return { success: false, error_type: response.error_type };
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Erreur lors de l\'inscription',
        conflict: null,
      }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      // Extract conflict data from 409 responses
      if (err instanceof ApiError && err.statusCode === 409 && err.response) {
        const resp = err.response as ApiConflictResponse;
        if (resp.conflict) {
          setState(prev => ({
            ...prev,
            loading: false,
            error: resp.message || 'Ce compte existe déjà',
            conflict: resp.conflict ?? null,
          }));
          return { success: false, error_type: resp.error_type };
        }
      }

      const message = getErrorMessage(err, 'l\'inscription');
      setState(prev => ({ ...prev, loading: false, error: message, conflict: null }));
      return { success: false };
    }
  }, [accountType]);

  const verifyOtp = useCallback(async (otpCode: string) => {
    if (!state.processToken) {
      return { success: false, error: 'Session invalide' };
    }
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await mobileRegistrationService.verifyOtp(state.processToken, otpCode);

      if (response.success) {
        if (response.status === 'completed' && response.callback_token) {
          setState(prev => ({ ...prev, status: 'completed', loading: false }));
          return { success: true, completed: true, callback_token: response.callback_token };
        }

        setState(prev => ({ ...prev, status: 'pending_password', loading: false }));
        return { success: true, completed: false };
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Code incorrect',
      }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      const message = getErrorMessage(err, 'la vérification');
      setState(prev => ({ ...prev, loading: false, error: message }));
      return { success: false };
    }
  }, [state.processToken]);

  const completeRegistration = useCallback(async (password: string) => {
    if (!state.processToken) {
      return { success: false, error: 'Session invalide' };
    }
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await mobileRegistrationService.complete(state.processToken, password);

      if (response.success && response.callback_token) {
        setState(prev => ({ ...prev, status: 'completed', loading: false }));
        return { success: true, callback_token: response.callback_token };
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Erreur lors de la finalisation',
      }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      const message = getErrorMessage(err, 'la finalisation');
      setState(prev => ({ ...prev, loading: false, error: message }));
      return { success: false };
    }
  }, [state.processToken]);

  const completePhoneOnlyRegistration = useCallback(async () => {
    if (!state.processToken) {
      return { success: false, error: 'Session invalide' };
    }
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await mobileRegistrationService.completePhoneOnly(state.processToken);

      if (response.success && response.callback_token) {
        setState(prev => ({ ...prev, status: 'completed', loading: false }));
        return { success: true, callback_token: response.callback_token };
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Erreur lors de la finalisation',
      }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      const message = getErrorMessage(err, 'la finalisation');
      setState(prev => ({ ...prev, loading: false, error: message }));
      return { success: false };
    }
  }, [state.processToken]);

  const resendOtp = useCallback(async () => {
    if (!state.processToken) {
      return { success: false, error: 'Session invalide' };
    }
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await mobileRegistrationService.resendOtp(state.processToken);
      setState(prev => ({
        ...prev,
        loading: false,
        otpMethod: response.otp_method || prev.otpMethod,
        otpSentTo: response.otp_sent_to || prev.otpSentTo,
      }));
      return { 
        success: response.success, 
        cooldown: response.cooldown_remaining,
        otp_code_dev: response.otp_code_dev,
        otp_method: response.otp_method,
        otp_sent_to: response.otp_sent_to,
      };
    } catch (err) {
      const message = getErrorMessage(err, 'le renvoi');
      setState(prev => ({ ...prev, loading: false, error: message }));
      return { success: false };
    }
  }, [state.processToken]);

  const reset = useCallback(() => {
    setState({
      processToken: null,
      status: 'idle',
      loading: false,
      error: null,
      conflict: null,
      otpMethod: null,
      otpSentTo: null,
    });
    setFormData({});
    setAccountType('email');
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null, conflict: null }));
  }, []);

  return {
    processToken: state.processToken,
    status: state.status,
    formData,
    loading: state.loading,
    error: state.error,
    conflict: state.conflict,
    otpMethod: state.otpMethod,
    otpSentTo: state.otpSentTo,
    isCompleted: state.status === 'completed',
    hasConflict: state.conflict !== null,
    
    accountType,
    setAccountType,
    isPhoneOnly: accountType === 'phone-only',

    updateFormData,
    initRegistration,
    verifyOtp,
    completeRegistration,
    completePhoneOnlyRegistration,
    resendOtp,
    reset,
    clearError,
  };
}

export default useMobileRegistration;
